/** Every tool in the product, declared once.
 *
 * This file is the product's action surface. The Tools overlay is generated
 * from it, so adding a line here is the whole job of surfacing a feature —
 * there is no second place to register it and no component to edit.
 */
import {
  CalendarDays,
  Circle,
  Columns2,
  DatabaseBackup,
  FileDown,
  FilePlus2,
  Filter,
  FolderTree,
  Globe,
  Keyboard,
  KeyRound,
  MessageCircleQuestion,
  Network,
  Orbit as OrbitIcon,
  PackageSearch,
  PanelLeft,
  PanelRight,
  PenLine,
  PlusCircle,
  RefreshCw,
  Search,
  Settings,
  Star,
  Upload,
  X,
} from "lucide-react";

import { workspace } from "@/components/workspace/controller";
import { createMarkdownNote, openDailyNote } from "@/lib/create-note";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

import { defineTool, type ToolContext } from "./tool-registry";

// ── Create ───────────────────────────────────────────────────────────────────

defineTool({
  id: "create.note",
  label: "Note",
  group: "create",
  icon: FilePlus2,
  hint: "A new markdown note",
  run: () => void createMarkdownNote(),
});

// One button called "Canvas" made ONE kind of note and never said so. Pages or
// an infinite surface, ruled or grid or dots, six paper colours — all of it
// existed, and none of it was reachable from the tools menu, so the product
// looked like it had a single canvas. This opens the chooser, where every one
// of those is a picture you can see before you commit to it.
//
// There is deliberately NO "today's handwritten page" beside it. A dated
// white sheet is one of the things this chooser makes, not a second feature,
// and offering it as its own button implied a daily-page workflow the product
// does not have.
defineTool({
  id: "create.canvas",
  label: "Handwritten",
  group: "create",
  icon: PenLine,
  hint: "Pages or infinite, ruled, grid or dots, any paper colour",
  run: () => useShell.getState().requestCreate("note"),
});


defineTool({
  id: "create.daily",
  label: "Daily",
  group: "create",
  icon: CalendarDays,
  hint: "Today's note, created if it does not exist",
  run: () => void openDailyNote(),
});

defineTool({
  id: "create.upload",
  label: "Upload",
  group: "create",
  icon: Upload,
  hint: "Ingest a file from disk",
  run: (ctx) => ctx.actions.pickUpload(),
});

defineTool({
  id: "create.website",
  label: "Website",
  group: "create",
  icon: Globe,
  hint: "Ingest a page by URL",
  run: (ctx) => ctx.actions.addWebsite(),
});

// ── Find ─────────────────────────────────────────────────────────────────────

defineTool({
  id: "find.search",
  label: "Search",
  group: "find",
  icon: Search,
  shortcut: "/",
  hint: "Search every document",
  run: (ctx) => ctx.actions.openPalette(),
});

defineTool({
  id: "find.folders",
  label: "Folders",
  group: "find",
  icon: FolderTree,
  hint: "The whole library as folders",
  run: () => workspace.openHome(),
});

defineTool({
  id: "find.ask",
  label: "Ask",
  group: "find",
  icon: MessageCircleQuestion,
  hint: "Ask about the document in focus",
  when: (ctx) => ctx.doc !== null,
  run: (ctx) => {
    const doc = ctx.doc;
    if (!doc) return;
    useTabs.getState().openAsk(
      doc.kind === "markdown"
        ? { scopeNodeIds: [doc.id], title: `ask · ${doc.title}` }
        : { scopeDocIds: [doc.id], title: `ask · ${doc.title}` },
    );
  },
});

defineTool({
  id: "find.filter",
  label: "Filter",
  group: "find",
  icon: Filter,
  hint: "Narrow the library by kind",
  run: () => workspace.openHome(),
});

// ── Graph ────────────────────────────────────────────────────────────────────

defineTool({
  id: "graph.atlas",
  label: "Atlas",
  group: "graph",
  icon: Network,
  hint: "The whole graph",
  run: () => workspace.openGraph(),
});

defineTool({
  id: "graph.orbit",
  label: "Orbit",
  group: "graph",
  icon: OrbitIcon,
  hint: "Documents around a centre",
  run: () => workspace.openOrbit(),
});

defineTool({
  id: "graph.concept",
  label: "Concept",
  group: "graph",
  icon: PlusCircle,
  hint: "Declare a concept of your own",
  run: () => workspace.openGraph(),
});

defineTool({
  id: "graph.connect",
  label: "Connect",
  group: "graph",
  icon: Circle,
  hint: "Draw a typed edge between two nodes",
  run: () => workspace.openGraph(),
});

defineTool({
  id: "graph.pin",
  label: "Pins",
  group: "graph",
  icon: Star,
  hint: "The nodes you are keeping in view",
  // Opens the PANEL. It used to call openGraph(), which showed the atlas and
  // left the person to find the pins themselves — the tile named a place and
  // then did not go there.
  run: () => useShell.getState().openPins(),
});

defineTool({
  id: "graph.recompute",
  label: "Recompute",
  group: "graph",
  icon: RefreshCw,
  hint: "Rebuild orbits from the current graph",
  run: () => workspace.openOrbit(),
});

// ── Data ─────────────────────────────────────────────────────────────────────

defineTool({
  id: "data.export",
  label: "Export",
  group: "data",
  icon: PackageSearch,
  hint: "Smart export — pick what leaves",
  run: (ctx) => ctx.actions.smartExport(),
});

defineTool({
  id: "data.pdf",
  label: "PDF",
  group: "data",
  icon: FileDown,
  hint: "Download the document in focus as PDF",
  when: (ctx) => ctx.doc !== null,
  run: (ctx) => {
    if (ctx.doc) window.open(`/api/documents/${ctx.doc.id}/annotated-pdf`, "_blank", "noopener");
  },
});

defineTool({
  id: "data.backups",
  label: "Backups",
  group: "data",
  icon: DatabaseBackup,
  run: (ctx) => ctx.actions.manageBackups(),
});

defineTool({
  id: "data.tokens",
  label: "Tokens",
  group: "data",
  icon: KeyRound,
  hint: "Access tokens for agents and the MCP client",
  run: (ctx) => ctx.actions.manageTokens(),
});

defineTool({
  id: "data.vault",
  label: "Vault",
  group: "data",
  icon: FolderTree,
  hint: "The on-device folder your files really live in",
  run: () => workspace.openHome(),
});

// ── Workspace ────────────────────────────────────────────────────────────────

defineTool({
  id: "ws.files",
  label: "Files",
  group: "workspace",
  icon: Columns2,
  hint: "The folder view, as a panel",
  run: () => workspace.openFiles(),
});

defineTool({
  id: "ws.closeAll",
  label: "Close all",
  group: "workspace",
  icon: X,
  hint: "Close every open tab",
  run: () => workspace.closeAll(),
});

defineTool({
  id: "ws.library",
  label: "Library",
  group: "workspace",
  icon: PanelLeft,
  shortcut: "[",
  hint: "Show or hide the file panel",
  run: () => useShell.getState().toggleLibrary(),
});

defineTool({
  id: "ws.context",
  label: "Context",
  group: "workspace",
  icon: PanelRight,
  shortcut: "]",
  hint: "Show or hide the context panel",
  run: () => useShell.getState().toggleContext(),
});

defineTool({
  id: "ws.settings",
  label: "Settings",
  group: "workspace",
  icon: Settings,
  run: () => useShell.getState().openSettings(),
});

defineTool({
  id: "ws.keys",
  label: "Keys",
  group: "workspace",
  icon: Keyboard,
  hint: "Every keyboard shortcut",
  // Opens the SHORTCUT SHEET. It used to call `openPalette()` — the tile
  // promised the keyboard map and opened search instead, which is the worst
  // kind of wrong: it does something, so nothing looks broken.
  run: () => useShell.getState().openShortcuts(),
});

/** Imported for its side effects; this keeps the import from being elided. */
export const TOOLS_REGISTERED = true;

export type { ToolContext };
